// tauri-web.ts — the ONE web-safe Tauri surface. Every file that used to import `invoke`,
// `platform`, or `listen` from `@tauri-apps/*` imports them from HERE instead. On desktop/iOS
// these are the real Tauri APIs; in a browser they transparently fall back to the web runtime.
//
// This is what makes the SAME frontend run as web + macOS + iOS from one codebase: the UI,
// helpers, and brain calls don't change — only this boundary knows which surface it's on.

import { invoke as tauriInvoke } from "@tauri-apps/api/core";
import { listen as tauriListen, type EventCallback, type UnlistenFn } from "@tauri-apps/api/event";
import { platform as tauriPlatform } from "@tauri-apps/plugin-os";
import { isTauri, makeWebInvoke, WebStore } from "./web-runtime";
import { makeWebCommands } from "./web-commands";

const APP_SLUG = "solo-journal";

// One WebStore for the whole app (the browser's stand-in for native SQLite).
export const webStore = new WebStore(APP_SLUG);

export const invoke: typeof tauriInvoke = isTauri()
  ? tauriInvoke
  : (makeWebInvoke(APP_SLUG, webStore, makeWebCommands(webStore)) as typeof tauriInvoke);

/** Synchronous platform tag. Returns "web" in a browser; the real Tauri value on native. */
export function platform(): string {
  return isTauri() ? tauriPlatform() : "web";
}

/** Event listener. On web there are no native/deep-link events, so this is a no-op. */
export async function listen<T>(event: string, cb: EventCallback<T>): Promise<UnlistenFn> {
  if (isTauri()) return tauriListen<T>(event, cb);
  return async () => {};
}
